import { Option, Schema } from "effect"
import type { CloudSignIn } from "./cloud-api"

const key = "omm.cloud.sign-in"
const callback = "/auth/return"
const Pending = Schema.Struct({
  state: Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{43}$/)),
  clientID: Schema.String,
  audience: Schema.String,
  returnPath: Schema.String,
  verificationUrl: Schema.String,
  userCode: Schema.String,
  interval: Schema.Int.check(Schema.isGreaterThan(0)),
  expiresAt: Schema.Number,
  returned: Schema.Boolean,
})
export type CloudPendingSignIn = Schema.Schema.Type<typeof Pending>

export class CloudSignInError extends Error {
  constructor(readonly code: "sign_in_storage" | "sign_in_return" | "expired_token" | "access_denied" | "cancelled") {
    super(code)
  }
}

export function createCloudSignIn(browser: {
  url: () => URL
  local: () => Storage
  session: () => Storage
  navigate: (url: string) => void
  replace: (path: string) => void
}) {
  function stored() {
    const raw = browser.session().getItem(key)
    if (!raw) return
    const parsed = Schema.decodeUnknownOption(Schema.fromJsonString(Pending))(raw)
    if (Option.isNone(parsed)) throw new CloudSignInError("sign_in_return")
    return parsed.value
  }
  function storage<A>(run: () => A): A {
    try {
      return run()
    } catch (error) {
      if (error instanceof CloudSignInError) throw error
      throw new CloudSignInError("sign_in_storage")
    }
  }
  function clear(all = false) {
    storage(() => {
      const raw = browser.session().getItem(key)
      browser.session().removeItem(key)
      const parsed = raw ? Schema.decodeUnknownOption(Schema.fromJsonString(Pending))(raw) : Option.none()
      if (all || (Option.isSome(parsed) && browser.local().getItem(key) === parsed.value.state))
        browser.local().removeItem(key)
    })
  }
  function active(pending: CloudPendingSignIn) {
    return storage(() => browser.local().getItem(key) === pending.state && stored()?.state === pending.state)
  }
  function save(pending: CloudPendingSignIn) {
    storage(() => {
      browser.session().setItem(key, JSON.stringify(pending))
      if (browser.session().getItem(key) !== JSON.stringify(pending)) throw new CloudSignInError("sign_in_storage")
    })
  }
  function path(value: string) {
    const current = browser.url()
    const url = URL.parse(value, current.origin)
    if (
      !value.startsWith("/") ||
      value.startsWith("//") ||
      value.includes("\\") ||
      !url ||
      url.origin !== current.origin ||
      /^\/(?:auth|api|v1|workspace)(?:\/|$)/.test(url.pathname)
    )
      return "/"
    return url.pathname + url.search + url.hash
  }
  return {
    check() {
      storage(() => {
        for (const target of [browser.session(), browser.local()]) {
          target.setItem(`${key}.check`, "1")
          if (target.getItem(`${key}.check`) !== "1") throw new CloudSignInError("sign_in_storage")
          target.removeItem(`${key}.check`)
        }
      })
    },
    start(attempt: CloudSignIn, audience: string, clientID: string) {
      const state = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))))
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replaceAll("=", "")
      const url = new URL(attempt.verificationUrl)
      url.searchParams.set("web_return_to", new URL(callback, browser.url().origin).href)
      url.searchParams.set("web_state", state)
      const pending = {
        ...attempt,
        state,
        clientID,
        audience,
        verificationUrl: url.href,
        returnPath: path(browser.url().pathname + browser.url().search + browser.url().hash),
        returned: false,
      }
      save(pending)
      storage(() => {
        browser.local().setItem(key, state)
        if (browser.local().getItem(key) !== state) throw new CloudSignInError("sign_in_storage")
      })
      browser.navigate(pending.verificationUrl)
      return pending
    },
    resume() {
      return storage(() => {
        const url = browser.url()
        const pending = stored()
        if (url.pathname === callback) {
          const result = new URLSearchParams(url.hash.slice(1))
          const valid =
            !url.search &&
            result.size === 2 &&
            result.getAll("state").length === 1 &&
            result.getAll("result").length === 1 &&
            pending &&
            active(pending) &&
            result.get("state") === pending.state &&
            ["approved", "denied", "cancelled", "expired"].includes(result.get("result") ?? "")
          browser.replace(valid ? path(pending.returnPath) : "/")
          if (!valid) {
            clear()
            throw new CloudSignInError("sign_in_return")
          }
          if (pending.expiresAt <= Date.now() || result.get("result") === "expired") {
            clear()
            throw new CloudSignInError("expired_token")
          }
          if (result.get("result") !== "approved") {
            clear()
            throw new CloudSignInError(result.get("result") === "denied" ? "access_denied" : "cancelled")
          }
          const resumed = { ...pending, returned: true }
          save(resumed)
          return resumed
        }
        if (!pending) return
        if (!active(pending)) {
          clear()
          return
        }
        if (pending.expiresAt <= Date.now()) {
          clear()
          throw new CloudSignInError("expired_token")
        }
        return pending
      })
    },
    reopen(pending: CloudPendingSignIn) {
      if (!active(pending)) throw new CloudSignInError("sign_in_return")
      if (pending.expiresAt <= Date.now()) throw new CloudSignInError("expired_token")
      browser.navigate(pending.verificationUrl)
    },
    active,
    clear,
  }
}
